import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Shield, Lock, Link2, Target, Copy, Check, 
  ChevronDown, ArrowLeft, Crosshair, ExternalLink
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { vaultConfig } from "../data/mock";
import { Link } from "react-router-dom";

const KNOWN_TARGETS = [
  { value: 'MACRO', label: 'MACRO (Charles D. King)' },
  { value: 'HOORAE', label: 'HOORAE (Issa Rae)' },
  { value: 'sdiazmatos', label: 'S. Diaz-Matos (Executive)' },
  { value: 'tylerperry', label: 'Tyler Perry Studios' },
  { value: 'blumhouse', label: 'Blumhouse Productions' },
  { value: 'trailerpark', label: 'Trailer Park Group' },
  { value: 'lionsgate', label: 'Lionsgate Entertainment' },
  { value: 'a24', label: 'A24 Films' },
  { value: 'hbo', label: 'HBO / Warner Bros Discovery' },
  { value: 'netflix', label: 'Netflix Music' },
  { value: 'hulu', label: 'Hulu Originals' },
  { value: 'amazon', label: 'Amazon Studios' },
  { value: 'disney', label: 'Disney Music Group' },
  { value: 'paramount', label: 'Paramount Global' },
  { value: 'sony', label: 'Sony Pictures Entertainment' },
  { value: 'universal', label: 'Universal Music Sync' },
  { value: 'warner', label: 'Warner Music Sync' },
  { value: 'fox', label: 'Fox Entertainment' },
  { value: 'starz', label: 'Starz / Lionsgate+' },
  { value: 'peacock', label: 'Peacock / NBCUniversal' },
  { value: 'bet', label: 'BET Networks' },
];

const DESTINATIONS = [
  { value: '/vault', label: 'Supervisor Vault', desc: 'Password-protected exclusive assets' },
  { value: '/catalog', label: 'Public Catalog', desc: 'Full catalog browse' },
  { value: '/', label: 'Homepage', desc: 'Liability Shield landing' },
  { value: '/roster', label: 'Artist Roster', desc: 'Producer profiles' },
  { value: '/licensing', label: 'Licensing Page', desc: 'Sync licensing terms' },
];

const LinkGeneratorPage = () => {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState(false);

  const [targetMode, setTargetMode] = useState('known');
  const [selectedTarget, setSelectedTarget] = useState('');
  const [customTarget, setCustomTarget] = useState('');
  const [destination, setDestination] = useState('/vault');
  const [suffix, setSuffix] = useState('');
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const m = document.createElement('meta');
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('itzgreen_link_history') || '[]');
      setHistory(saved);
    } catch { /* ignore */ }
  }, []);

  const handleAuth = (e) => {
    e.preventDefault();
    if (password === vaultConfig.password) {
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
    }
  };

  const getRefCode = () => {
    const base = targetMode === 'known' ? selectedTarget : customTarget.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();
    return suffix ? `${base}_${suffix}` : base;
  };

  const getBaseUrl = () => {
    return typeof window !== 'undefined' ? window.location.origin : 'https://itzgreenmusic.com';
  };

  const getGeneratedUrl = () => {
    const ref = getRefCode();
    if (!ref) return '';
    return `${getBaseUrl()}${destination}?ref=${ref}`;
  };

  const getDecodedTarget = () => {
    if (targetMode === 'known') {
      const found = KNOWN_TARGETS.find(t => t.value === selectedTarget);
      return found ? found.label : '';
    }
    return customTarget || '';
  };

  const handleCopy = async () => {
    const url = getGeneratedUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      const entry = {
        ref: getRefCode(),
        target: getDecodedTarget(),
        destination,
        url,
        createdAt: new Date().toISOString()
      };
      const updated = [entry, ...history].slice(0, 50);
      setHistory(updated);
      localStorage.setItem('itzgreen_link_history', JSON.stringify(updated));
    } catch {
      // Fallback copy
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isValid = getRefCode().length > 0;

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-[rgb(10,10,12)] flex items-center justify-center" data-testid="link-gen-auth-gate">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[rgb(18,18,22)] border border-[rgba(255,255,255,0.08)] rounded-lg p-8 w-full max-w-md"
        >
          <div className="flex items-center gap-3 mb-6">
            <Lock size={20} className="text-[rgb(218,255,1)]" />
            <h1 className="text-white font-mono text-sm tracking-[0.15em] font-bold">LINK GENERATOR ACCESS</h1>
          </div>
          <form onSubmit={handleAuth}>
            <Input
              type="password"
              placeholder="Enter access code"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(false); }}
              className="bg-[rgb(26,26,32)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)] mb-4"
              data-testid="link-gen-password-input"
            />
            {error && <p className="text-red-500 text-xs mb-3">Access denied</p>}
            <Button type="submit" className="w-full bg-[rgb(218,255,1)] text-black font-bold hover:bg-[rgb(198,235,0)]" data-testid="link-gen-auth-btn">
              AUTHENTICATE
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[rgb(10,10,12)] text-white" data-testid="link-generator-page">
      {/* Header */}
      <div className="border-b border-[rgba(255,255,255,0.06)] bg-[rgb(14,14,18)]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/analytics" className="text-[rgb(100,100,110)] hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <div>
              <p className="text-[10px] text-[rgb(218,255,1)] tracking-[0.2em] font-bold">OUTBOUND OPERATIONS</p>
              <h1 className="text-white font-mono text-lg tracking-[0.05em] font-bold">PITCH LINK GENERATOR</h1>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge variant="outline" className="border-[rgba(218,255,1,0.3)] text-[rgb(218,255,1)] text-[10px]">
              POSTHOG TRACKED
            </Badge>
            <Badge variant="outline" className="border-[rgb(63,63,63)] text-[rgb(161,161,170)] text-[10px]">
              {history.length} LINKS GENERATED
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Builder Panel */}
          <div className="lg:col-span-3 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[rgb(18,18,22)] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden"
            >
              <div className="p-5 border-b border-[rgba(255,255,255,0.06)]">
                <h2 className="text-xs tracking-[0.15em] font-bold text-white flex items-center gap-2">
                  <Target size={14} className="text-[rgb(218,255,1)]" />
                  SELECT TARGET
                </h2>
              </div>
              <div className="p-5 space-y-4">
                {/* Target Mode Toggle */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setTargetMode('known')}
                    className={`px-4 py-2 text-[11px] font-bold tracking-wider rounded transition-all ${
                      targetMode === 'known'
                        ? 'bg-[rgb(218,255,1)] text-black'
                        : 'bg-[rgb(26,26,32)] text-[rgb(161,161,170)] border border-[rgb(63,63,63)] hover:border-[rgb(100,100,110)]'
                    }`}
                    data-testid="target-mode-known"
                  >
                    KNOWN TARGETS
                  </button>
                  <button
                    onClick={() => setTargetMode('custom')}
                    className={`px-4 py-2 text-[11px] font-bold tracking-wider rounded transition-all ${
                      targetMode === 'custom'
                        ? 'bg-[rgb(218,255,1)] text-black'
                        : 'bg-[rgb(26,26,32)] text-[rgb(161,161,170)] border border-[rgb(63,63,63)] hover:border-[rgb(100,100,110)]'
                    }`}
                    data-testid="target-mode-custom"
                  >
                    CUSTOM TARGET
                  </button>
                </div>

                {targetMode === 'known' ? (
                  <div className="relative">
                    <select
                      value={selectedTarget}
                      onChange={(e) => setSelectedTarget(e.target.value)}
                      className="w-full bg-[rgb(26,26,32)] border border-[rgb(63,63,63)] text-white rounded px-4 py-3 text-sm appearance-none cursor-pointer focus:border-[rgb(218,255,1)] focus:outline-none transition-colors"
                      data-testid="target-select"
                    >
                      <option value="">Select executive / company...</option>
                      {KNOWN_TARGETS.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(100,100,110)] pointer-events-none" />
                  </div>
                ) : (
                  <Input
                    placeholder="Enter target name (e.g., john_martinez)"
                    value={customTarget}
                    onChange={(e) => setCustomTarget(e.target.value)}
                    className="bg-[rgb(26,26,32)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)]"
                    data-testid="custom-target-input"
                  />
                )}

                {/* Campaign Suffix */}
                <div>
                  <label className="text-[10px] text-[rgb(100,100,110)] tracking-[0.15em] block mb-2">CAMPAIGN SUFFIX (OPTIONAL)</label>
                  <Input
                    placeholder="e.g., Q2, TRAILER, PITCH_1"
                    value={suffix}
                    onChange={(e) => setSuffix(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
                    className="bg-[rgb(26,26,32)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)]"
                    data-testid="campaign-suffix-input"
                  />
                </div>
              </div>
            </motion.div>

            {/* Destination */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[rgb(18,18,22)] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden"
            >
              <div className="p-5 border-b border-[rgba(255,255,255,0.06)]">
                <h2 className="text-xs tracking-[0.15em] font-bold text-white flex items-center gap-2">
                  <Link2 size={14} className="text-[rgb(218,255,1)]" />
                  ROUTING DESTINATION
                </h2>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {DESTINATIONS.map(d => (
                    <button
                      key={d.value}
                      onClick={() => setDestination(d.value)}
                      className={`text-left p-4 rounded-lg border transition-all ${
                        destination === d.value
                          ? 'bg-[rgba(218,255,1,0.06)] border-[rgba(218,255,1,0.4)]'
                          : 'bg-[rgb(26,26,32)] border-[rgb(45,45,50)] hover:border-[rgb(80,80,90)]'
                      }`}
                      data-testid={`dest-${d.value.replace('/', '') || 'home'}`}
                    >
                      <p className={`text-xs font-bold tracking-wider ${
                        destination === d.value ? 'text-[rgb(218,255,1)]' : 'text-white'
                      }`}>{d.label}</p>
                      <p className="text-[10px] text-[rgb(100,100,110)] mt-1">{d.desc}</p>
                      <p className="text-[10px] font-mono text-[rgb(63,63,63)] mt-1">{d.value}</p>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Output */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`rounded-lg overflow-hidden border transition-all ${
                isValid 
                  ? 'bg-[rgb(18,18,22)] border-[rgba(218,255,1,0.3)]' 
                  : 'bg-[rgb(16,16,18)] border-[rgba(255,255,255,0.05)] opacity-60'
              }`}
            >
              <div className="p-5 border-b border-[rgba(255,255,255,0.06)] flex items-center justify-between">
                <h2 className="text-xs tracking-[0.15em] font-bold text-white flex items-center gap-2">
                  <Crosshair size={14} className="text-[rgb(218,255,1)]" />
                  GENERATED PITCH LINK
                </h2>
                {isValid && (
                  <Badge variant="outline" className="border-[rgba(218,255,1,0.3)] text-[rgb(218,255,1)] text-[10px]">
                    READY TO DEPLOY
                  </Badge>
                )}
              </div>
              <div className="p-5 space-y-4">
                {isValid ? (
                  <>
                    {/* URL Display */}
                    <div className="bg-[rgb(10,10,12)] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 font-mono text-sm break-all">
                      <span className="text-[rgb(100,100,110)]">{getBaseUrl()}{destination}</span>
                      <span className="text-[rgb(218,255,1)]">?ref={getRefCode()}</span>
                    </div>

                    {/* Copy Button */}
                    <Button
                      onClick={handleCopy}
                      className={`w-full font-bold text-sm tracking-wider transition-all ${
                        copied 
                          ? 'bg-green-600 text-white hover:bg-green-600' 
                          : 'bg-[rgb(218,255,1)] text-black hover:bg-[rgb(198,235,0)]'
                      }`}
                      data-testid="copy-link-btn"
                    >
                      {copied ? (
                        <><Check size={16} className="mr-2" /> COPIED TO CLIPBOARD</>
                      ) : (
                        <><Copy size={16} className="mr-2" /> COPY PITCH LINK</>
                      )}
                    </Button>

                    {/* Decode Preview */}
                    <div className="bg-[rgb(26,26,32)] rounded-lg p-4 space-y-2">
                      <p className="text-[10px] text-[rgb(100,100,110)] tracking-[0.15em]">DECODER PREVIEW</p>
                      <div className="grid grid-cols-2 gap-3 text-[11px]">
                        <div>
                          <p className="text-[rgb(80,80,90)]">REF CODE</p>
                          <p className="text-[rgb(218,255,1)] font-mono font-bold">{getRefCode()}</p>
                        </div>
                        <div>
                          <p className="text-[rgb(80,80,90)]">DECODED TARGET</p>
                          <p className="text-white font-medium">{getDecodedTarget()}</p>
                        </div>
                        <div>
                          <p className="text-[rgb(80,80,90)]">DESTINATION</p>
                          <p className="text-white font-mono">{destination}</p>
                        </div>
                        <div>
                          <p className="text-[rgb(80,80,90)]">TRACKING</p>
                          <p className="text-green-400">PostHog + SQLite</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-[rgb(80,80,90)] text-xs">Select a target and destination to generate a pitch link</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* History Sidebar */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[rgb(18,18,22)] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden sticky top-6"
            >
              <div className="p-5 border-b border-[rgba(255,255,255,0.06)] flex justify-between items-center">
                <h2 className="text-xs tracking-[0.15em] font-bold text-white">DEPLOYMENT LOG</h2>
                <Badge variant="outline" className="border-[rgb(63,63,63)] text-[rgb(100,100,110)] text-[10px]">
                  {history.length} LINKS
                </Badge>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {history.length > 0 ? (
                  <div className="divide-y divide-[rgba(255,255,255,0.04)]">
                    {history.map((item, i) => (
                      <div key={i} className="p-4 hover:bg-[rgba(255,255,255,0.02)] transition-colors group">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-[rgb(218,255,1)] text-[10px] font-mono font-bold">?ref={item.ref}</span>
                          <button
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(item.url);
                              } catch { /* fallback */ }
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Copy size={12} className="text-[rgb(100,100,110)] hover:text-white" />
                          </button>
                        </div>
                        <p className="text-white text-xs font-medium">{item.target}</p>
                        <p className="text-[rgb(80,80,90)] text-[10px] font-mono mt-1">{item.destination}</p>
                        <p className="text-[rgb(63,63,63)] text-[10px] mt-1">
                          {new Date(item.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Shield size={24} className="mx-auto text-[rgb(63,63,63)] mb-3" />
                    <p className="text-[rgb(80,80,90)] text-xs">No links generated yet</p>
                    <p className="text-[rgb(63,63,63)] text-[10px] mt-1">Generated links will appear here</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Quick Nav */}
            <div className="mt-4 flex gap-2">
              <Link to="/admin/analytics" className="flex-1">
                <Button variant="outline" className="w-full border-[rgb(63,63,63)] text-[rgb(161,161,170)] hover:text-white text-[10px] tracking-wider" data-testid="nav-analytics">
                  <ExternalLink size={12} className="mr-1.5" /> ANALYTICS
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkGeneratorPage;
